import React, { FC } from 'react'
import { css } from '@emotion/css'
import { FixedSizeTree as Tree } from 'react-vtree'
import { AutoSizer } from 'react-virtualized'

import { TreeNode } from './tree-node'
import { uuidv4 } from '../../../../util'
import { Dependency, TreeType } from './interfaces'
import { Organization } from '@contrast-security-inc/base-types'

interface TreePresenterProps {
  itemSize: number
  treeResult: Dependency[]
  allCollapsed: boolean
  searchString: string
  organization: Organization | undefined
  treeType: TreeType
}

export const TreePresenter: FC<TreePresenterProps> = ({
  itemSize,
  treeResult,
  allCollapsed,
  searchString,
  organization,
  treeType
}) => {
  const getNodeData = (node, nestingLevel) => ({
    data: {
      id: uuidv4(),
      isLeaf: node.children.length === 0,
      isOpenByDefault: allCollapsed,
      name: node.name,
      vulnerabilities: node.vulnerabilities || [],
      version: node.resolved,
      hasVulnerableDependency: node.hasVulnerableDependency,
      searchString,
      nestingLevel,
      score: node.score
    },
    nestingLevel,
    searchString,
    node
  })

  function* treeWalker() {
    // Step [1]: Define the root node of our tree. There can be one or
    // multiple nodes.

    for (const element of treeResult) {
      yield getNodeData(element, 0)
    }

    while (true) {
      // Step [2]: Get the parent component back. It will be the object
      // the `getNodeData` function constructed, so you can read any data from it.
      const parent = yield

      for (const item of parent.node.children) {
        // Step [3]: Yielding all the children of the provided component. Then we
        // will return for the step [2] with the first children.
        yield getNodeData(item, parent.nestingLevel + 1)
      }
    }
  }

  const treeContainer = css`
    width: 100%;
    flex: 1 1 auto;
    height: 65vh;
  `

  return (
    <div className={treeContainer} data-e2e="dependency-tree-container">
      <AutoSizer disableWidth>
        {({ height, width }) => (
          <Tree
            // @ts-ignore  TS-3.8.3 TS-8508
            treeWalker={treeWalker}
            itemSize={itemSize}
            height={height}
            width={width}
          >
            {/*
            // @ts-ignore  TS-3.8.3 */}
            {(props) => (
              <TreeNode
                {...props}
                organization={organization}
                treeType={treeType}
              />
            )}
          </Tree>
        )}
      </AutoSizer>
    </div>
  )
}
